import { PayMessage } from '@/app/components/ChatMessage/PayMessage'
import { Flex } from '@/app/components/Flex/Flex'
import { MessageUI } from '@/app/components/MessageUI/MesssageUI'
import { useChat } from '@/app/state/chat'
import { useSession } from '@/app/state/session'
import type { FunctionalComponent } from 'preact'
import { memo } from 'preact/compat'
import { useEffect, useMemo, useRef } from 'preact/hooks'
import { useShallow } from 'zustand/shallow'

const ChatMessageBase: FunctionalComponent<{
  id: string
  isLast?: boolean
}> = props => {
  const loadingFlag = useRef(false)
  const hasPayment = useSession(s => s.user?.hasPayment)
  const hasLoyaltyDiscount = useSession(s => s.user?.hasLoyaltyDiscount)
  const isLegacy = useSession(s => s.user?.pricingType === 'legacy')

  const messageCount = useSession(s => s.user?.messagesCount)

  const message = useChat(useShallow(s => {
    const m = s.data[props.id]

    if (!m) {
      return null
    }

    return {
      id: m.id,
      action: m.message.action,
      loading: m.state.loading,
      text: m.message.text,
      hidden: m.message.hidden,
      role: m.message.role,
      reading: !!m.message.reading,
      ctx: !!m.message.ctx,
    }
  }))

  useEffect(() => {
    if (!message) return

    if (props.isLast && (message?.loading || loadingFlag.current)) {
      loadingFlag.current = true
      document.getElementById(message.id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [message?.loading, message?.id, props.isLast])

  const data = useMemo(() => {
    return message ? parseResponse(message.text) : null
  }, [message?.text])

  if (!data || !message) return null

  return (
    <MessageUI.Container
      id={message.id}
      hidden={message.hidden}
    >
      {!message.hidden && (
        <>
          {message.reading && <MessageUI.Reading id={message.id} />}

          {message.action === 'access'
            ? (
              <PayMessage
                hasLoyaltyDiscount={hasLoyaltyDiscount}
                hasPayment={hasPayment}
                isLegacy={isLegacy}
              />
            )
            : (
              <Flex gap={15}>
                <MessageUI
                  loading={message.loading}
                  type={message.role}
                  text={data.text}
                />

                {props.isLast && hasPayment && messageCount === 0 && (
                  <PayMessage
                    hasLoyaltyDiscount={hasLoyaltyDiscount}
                    hasPayment={hasPayment}
                    isLegacy={isLegacy}
                  />
                )}
              </Flex>
            )}
        </>
      )}
    </MessageUI.Container>
  )
}

export const ChatMessage = memo(ChatMessageBase)

function parseResponse(raw: string) {
  const match = raw.match(/\[suggestions:\s*([^\]]+)\]/)

  const text = raw.replace(/\[suggestions:\s*[^\]]+\]/, '').trim()

  const suggestions = match
    ? match[1].split('|').map(s => s.trim()).filter(s => s.length > 3)
    : []

  return { text, suggestions }
}
